import React from "react";
import styled from "styled-components";

import { MOBILE_BREAKPOINT } from "../../constants";
import { isLightColor } from "../../colorUtils";
import { useEyedropper } from "../../contexts/EyedropperContext";
import { usePalette } from "../../contexts/PaletteContext";
import { noFocusOnMouseDown } from "../../utils";
import ColorEditor from "../ColorEditor";
import ToolPopover from "../ToolPopover";

function ColorPalette() {
  const {
    palette,
    activeColorIndex,
    setActiveColorIndex,
    setPaletteSlot,
    colorLocked,
  } = usePalette();
  return (
    <>
      {palette.map((hex, i) => (
        <Swatch
          key={i}
          index={i}
          hex={hex}
          active={i === activeColorIndex}
          locked={colorLocked}
          onSelect={colorLocked ? null : setActiveColorIndex}
          onEdit={colorLocked ? null : setPaletteSlot}
        />
      ))}
    </>
  );
}

// Mobile fallback: shows only the active swatch; tap opens a popover
// with the full palette in a 3-tall × 2-wide grid, right edge aligned
// to the trigger's right edge (`align="end"`, `side="top"`).
export function CompactColorPalette() {
  const {
    palette,
    activeColorIndex,
    setActiveColorIndex,
    setPaletteSlot,
    colorLocked,
  } = usePalette();
  const [open, setOpen] = React.useState(false);
  const activeHex = palette[activeColorIndex];

  const handleSelect = (idx) => {
    setActiveColorIndex(idx);
    setOpen(false);
  };

  return (
    <ToolPopover
      open={open}
      onOpenChange={setOpen}
      side="top"
      align="end"
      sideOffset={8}
      trigger={
        <CompactSwatch
          type="button"
          $hex={activeHex}
          onClick={() => setOpen((o) => !o)}
          aria-label={`Active color (${activeHex}) · tap to change`}
          title={`${activeHex} · tap to change`}
        />
      }
    >
      <Grid>
        {palette.map((hex, i) => (
          <Swatch
            key={i}
            index={i}
            hex={hex}
            active={i === activeColorIndex}
            locked={colorLocked}
            onSelect={colorLocked ? null : handleSelect}
            onEdit={colorLocked ? null : setPaletteSlot}
          />
        ))}
      </Grid>
    </ToolPopover>
  );
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  grid-auto-rows: auto;
  gap: 4px;
  justify-items: center;
`;

// Mirrors the desktop "active swatch" look: chrome-active square pad
// behind a circular pigment swatch. Sized to match the toolbar's
// ToolButton — 40px on mobile (where this is actually rendered),
// 50px otherwise as a fallback if anyone reuses it on desktop.
const CompactSwatch = styled.button.attrs({
  onMouseDown: noFocusOnMouseDown,
})`
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: var(--color-chrome-active);
  cursor: pointer;

  &::after {
    content: "";
    width: 80%;
    aspect-ratio: 1;
    border-radius: 50%;
    background: ${(p) => p.$hex};
  }

  &:focus-visible {
    outline: 2px solid var(--color-chrome-active);
    outline-offset: 2px;
  }
`;

const OuterWrap = styled.div`
  display: grid;
  place-items: center;
  width: 50px;
  aspect-ratio: 1;
  border-radius: 6px;
  background: ${(p) =>
    p.$active ? "var(--color-chrome-active)" : "transparent"};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 40px;
  }

  transition: background-color 120ms ease-out;

  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$active ? "var(--color-chrome-active)" : "var(--color-chrome-hover)"};
  }
`;

function Swatch({ index, hex, active, locked, onSelect, onEdit }) {
  const [editorOpen, setEditorOpen] = React.useState(false);
  const { active: eyedropperActive } = useEyedropper();

  const openEditor = (event) => {
    if (!onEdit) return;
    event.preventDefault();
    setEditorOpen(true);
  };

  const onLight = isLightColor(hex);
  const title = locked
    ? `${index + 1} · ${hex} · color locked (speedrun)`
    : onEdit
      ? `${index + 1} · ${hex} · double-click or right-click to edit`
      : `${index + 1} · ${hex}`;

  const swatchEl = (
    <OuterWrap $active={active} $locked={locked}>
      <SwatchWrap
        $hex={hex}
        $active={active}
        $locked={locked}
        $onLight={onLight}
      >
        <SwatchButton
          type="button"
          onClick={onSelect ? () => onSelect(index) : undefined}
          onDoubleClick={openEditor}
          onContextMenu={onEdit ? openEditor : (e) => e.preventDefault()}
          aria-label={`Color ${index + 1} (${hex})`}
          title={title}
          disabled={locked && !active}
          $locked={locked}
        >
          <Num $onLight={onLight}>{index + 1}</Num>
        </SwatchButton>
      </SwatchWrap>
    </OuterWrap>
  );

  if (!onEdit) return swatchEl;

  return (
    <ToolPopover
      open={editorOpen}
      onOpenChange={setEditorOpen}
      side="right"
      align="center"
      lockOpen={eyedropperActive}
      trigger={swatchEl}
    >
      <ColorEditor
        value={hex}
        onChange={(next) => onEdit(index, next)}
        onClose={() => setEditorOpen(false)}
      />
    </ToolPopover>
  );
}

// Circular swatches (pigment), distinct from the square tool buttons.
//
// One indicator at any state — no stacked rings:
//   idle:     soft 2px inset shadow, gives every swatch a visible boundary
//             against the chrome regardless of fill
//   selected: no inset; the OuterWrap's chrome-active background ring does
//             the "this one's picked" job
const SwatchWrap = styled.div`
  position: relative;
  width: 80%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: ${(p) => p.$hex};
  cursor: ${(p) => (p.$locked && !p.$active ? "default" : "pointer")};
  /* Locked + inactive swatches dim out so the active one reads as the only
     interactive thing. The active swatch stays full-strength so the player
     can see what color is selected for the current target. */
  opacity: ${(p) => (p.$locked && !p.$active ? 0.35 : 1)};
  box-shadow: ${(p) =>
    p.$active ? "none" : "inset 0 0 2px rgba(0, 0, 0, 0.3)"};
  z-index: ${(p) => (p.$active ? 1 : 0)};
  transition: opacity 200ms ease-out;
`;

const SwatchButton = styled.button.attrs({
  onMouseDown: noFocusOnMouseDown,
})`
  appearance: none;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: ${(p) => (p.disabled ? "default" : "pointer")};
`;

// Centered inside the circle — the geometry of a round swatch doesn't give
// a corner to tuck the hint in. Tool buttons (square) keep their bottom-
// right hint; different shape, different placement.
// Hidden on coarse-pointer devices for the same reason as toolbar Hints.
const Num = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: ${(p) =>
    p.$onLight ? "var(--color-text)" : "var(--color-selected-fg)"};
  opacity: 0.8;
  pointer-events: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;

  @media (pointer: coarse) {
    display: none;
  }
`;

export default ColorPalette;
